import { useQuery } from '@apollo/client';
import { Button, ButtonGroup, Tooltip, Typography, useTheme } from '@material-ui/core';
import { Sort, SortByAlpha } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { sortBy } from 'lodash';
import React from 'react';

import DocumentTitle from 'react-document-title';

import { buildStackedHorizontalBarOption } from 'shared/common/charts/options';

import { echarts, ReactEChartsCore } from '../common/charts/echarts-core';
import { usePortalTooltip } from '../common/charts/tooltip';
import { Duration, getDurationOptions } from '../common/duration';
import {
    buildStackedTooltipFormatter,
    clientActivityFields,
    defaultAggregateViewSelected,
    Field,
    FieldsSelected,
    funnelMetrics,
    getChartHeight,
    getFieldColors,
    getFieldLabel
} from '../common/metrics';
import { isoDate } from '../common/timestamp';
import { MetricsData, TEAM_METRICS } from '../graphql/queries/metrics';
import { TEAM, Team as TeamData } from '../graphql/queries/team';
import { useChartExport } from '../hooks/use-chart-export';
import { useLocalStorage } from '../hooks/use-local-storage';
import { internalClientIds as excludeClientIds, JobFilterTypes, JobType, jobTypesForFilter } from '../state';
import { ChartExportButtons } from './charts/chart-export-buttons';
import { DurationSelector } from './duration-selector';
import { Header } from './header';
import { JobsTypeFilter } from './jobs-type-filter';
import { Legend } from './metrics/legend';
import { styles } from './metrics/styles';

export const TeamMetrics: React.FC<{ id: string }> = ({ id }) => {
    const theme = useTheme();
    const durationPeriods = { days: 2, weeks: 2, months: 2, quarters: 4, years: 2, allTime: true };
    const durationOpts = getDurationOptions(durationPeriods);
    const defaultOption = durationOpts.find((opt) => opt.label === 'This Week');
    const [duration, setDuration] = useLocalStorage<Duration>('metrics-duration', defaultOption);
    const [jobTypes, setJobTypes] = useLocalStorage<JobFilterTypes>('metrics-job-types', 'placement-fees');
    const { data: teamData } = useQuery<{ team: TeamData }, { id: string }>(TEAM, { variables: { id } });
    const { data } = useQuery<
        { metrics: MetricsData[] },
        {
            userIds: string[];
            kinds: string[];
            jobTypes: JobType[];
            excludeClientIds: string[];
            startDate: string;
            endDate: string;
        }
    >(TEAM_METRICS, {
        skip: !teamData,
        variables: {
            endDate: isoDate(duration.end),
            excludeClientIds,
            jobTypes: jobTypesForFilter.get(jobTypes),
            kinds: clientActivityFields,
            startDate: isoDate(duration.start),
            userIds: teamData?.team.users.map((u) => u.user.id) ?? []
        }
    });
    const [selectedFields, setSelectedFields] = useLocalStorage<FieldsSelected>(
        'agg-metrics-fields',
        defaultAggregateViewSelected
    );
    const [hoveredField, setHoveredField] = React.useState<Field | undefined>(undefined);
    const [variant, setVariant] = useLocalStorage<'cost' | 'count'>('agg-metrics-variant', 'count');
    const [sort, setSort] = useLocalStorage<'alpha' | 'sum'>('agg-metrics-sort', 'alpha');
    const teamName = teamData?.team.name ?? 'team-metrics';
    const chartExport = useChartExport({ title: 'team-metrics', scope: teamName });

    const fields = clientActivityFields.filter((f) => selectedFields[f]);

    const chartModel = React.useMemo(() => {
        if (!teamData || !data) {
            return undefined;
        }
        const entries = teamData.team.users.map((teamUser) => {
            const userMetrics = funnelMetrics(
                data.metrics.filter((metric) => metric.user.id === teamUser.user.id),
                variant
            );
            const sum = fields.reduce((acc, field) => acc + (userMetrics[field] ?? 0), 0);
            return {
                metrics: userMetrics,
                sum,
                user: teamUser.user
            };
        });
        const sortedEntries = sortBy(entries, (entry) => (sort === 'alpha' ? entry.user.name : -entry.sum));
        const categories = sortedEntries.map((entry) => entry.user.name);
        const series = fields.map((field) => ({
            data: sortedEntries.map((entry) => entry.metrics[field] ?? 0),
            name: getFieldLabel(field)
        }));
        return {
            categories,
            entries: sortedEntries,
            series
        };
    }, [data, fields, sort, teamData, variant]);

    const tooltipFormatter = React.useMemo(() => {
        if (!chartModel) {
            return () => '';
        }
        return buildStackedTooltipFormatter({
            entries: chartModel.entries.map((entry) => ({ label: entry.user.name, metrics: entry.metrics })),
            fields,
            header: 'Team Member',
            series: chartModel.series
        });
    }, [chartModel, fields]);

    const tooltip = usePortalTooltip(
        {
            formatter: tooltipFormatter,
            trigger: 'item'
        },
        [tooltipFormatter]
    );

    const colors = getFieldColors(fields, hoveredField);

    const chartOptions = React.useMemo(() => {
        if (!chartModel || !chartModel.categories.length) {
            return undefined;
        }
        return buildStackedHorizontalBarOption({
            categories: chartModel.categories,
            colors,
            series: chartModel.series,
            tooltip
        });
    }, [chartModel, colors, tooltip]);

    const hasChartData = Boolean(chartModel && chartModel.categories.length > 0 && chartOptions);

    const handleVariantChange = (value: 'cost' | 'count') => () => {
        setVariant(value);
    };

    const handleSortChange = (value: 'alpha' | 'sum') => () => {
        setSort(value);
    };

    let content;
    if (!teamData) {
        content = <Skeleton variant="rect" />;
    } else {
        const chart = !data ? (
            <Skeleton variant="rect" />
        ) : hasChartData && chartOptions ? (
            <ReactEChartsCore
                ref={chartExport.chartRef}
                echarts={echarts}
                option={chartOptions}
                notMerge={true}
                lazyUpdate={true}
                style={{ height: getChartHeight(chartModel?.categories.length ?? 0) }}
                opts={{ renderer: 'svg' }}
            />
        ) : null;

        const canExport = Boolean(data && hasChartData);

        const tabs = (
            <div className="view-selectors">
                <ButtonGroup size="small" variant="text">
                    <Tooltip title="Sort Alphabetical">
                        <Button className={sort === 'alpha' ? '' : 'inactive'} onClick={handleSortChange('alpha')}>
                            <SortByAlpha fontSize="small" />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Sort by Activity">
                        <Button className={sort === 'sum' ? '' : 'inactive'} onClick={handleSortChange('sum')}>
                            <Sort fontSize="small" />
                        </Button>
                    </Tooltip>
                </ButtonGroup>
                <ButtonGroup size="small" variant="text">
                    <Tooltip title="Individual Action Counts">
                        <Button
                            className={variant === 'count' ? '' : 'inactive'}
                            onClick={handleVariantChange('count')}
                        >
                            Raw Counts
                        </Button>
                    </Tooltip>
                    <Tooltip title="Weighted sums based on estimated action time">
                        <Button className={variant === 'cost' ? '' : 'inactive'} onClick={handleVariantChange('cost')}>
                            Weighted Sums
                        </Button>
                    </Tooltip>
                </ButtonGroup>
            </div>
        );

        content = (
            <div className="chart-container">
                <div className="panel-header">
                    <div className="panel-title">
                        <Typography variant="h5">Team Metrics</Typography>
                    </div>
                    <div className="panel-options">
                        {tabs}
                        <ChartExportButtons
                            disabled={!canExport}
                            isExporting={chartExport.isExporting}
                            onExportPng={chartExport.exportAsPng}
                            onExportSvg={chartExport.exportAsSvg}
                        />
                    </div>
                </div>
                <div className="panel-content">
                    <div className="chart">{chart}</div>
                    <Legend
                        fields={clientActivityFields}
                        selected={selectedFields}
                        onFieldHover={setHoveredField}
                        onUpdateSelected={setSelectedFields}
                    />
                </div>
            </div>
        );
    }

    const title = `${teamData?.team.name ?? ''} Metrics`;

    return (
        <DocumentTitle title={title}>
            <div id="container">
                <Header title={title} />
                <div css={styles(theme)}>
                    <div className="metrics-container">
                        <div className="selectors">
                            <DurationSelector selected={duration} onSelect={setDuration} periods={durationPeriods} />
                            <JobsTypeFilter selectedFilter={jobTypes} onSelectFilter={setJobTypes} />
                        </div>
                        {content}
                    </div>
                </div>
            </div>
        </DocumentTitle>
    );
};
